/**
 * Confirmed (document-backed) event date helpers for the intake wizard.
 *
 * Event Intelligence reconciles fire report / FNOL / policy signals into
 * `event_intelligence.incident_date` (== `confirmed_event_date`). The wizard must not
 * default a first-of-month anomaly date over it, nor persist a date the user never typed
 * (RCA 2026-10-29: a defaulted 2023-03-01 shadowed the confirmed 2023-03-15).
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

function isoDay(v: unknown): string | undefined {
  return typeof v === 'string' && ISO_DATE.test(v) ? v.slice(0, 10) : undefined;
}

export function getConfirmedEventDate(data: any): string | undefined {
  return (
    isoDay(data?.event_intelligence?.incident_date) ||
    isoDay(data?.event_intelligence?.confirmed_event_date) ||
    isoDay(data?.resolved_case_metadata?.event_date) ||
    undefined
  );
}

/**
 * Start date to put in a save payload. A prefilled/defaulted value (not typed by the user)
 * must not be persisted: return the already-stored value unchanged instead.
 */
export function persistableEventStartDate(
  current: string,
  prefilled: boolean,
  data: any,
): string | undefined {
  return prefilled ? data?.event_start_date : current;
}

export interface AutoEventStart {
  value: string | undefined;
  /** true only when the value is a fresh anomaly default that should be written */
  persist: boolean;
  source: 'user' | 'confirmed' | 'anomaly' | 'none';
}

/** Precedence for the auto-detected start date: user value > confirmed EI date > anomaly default. */
export function pickAutoEventStart(args: {
  es?: string;
  confirmed?: string;
  anomalyStart?: string;
}): AutoEventStart {
  const { es, confirmed, anomalyStart } = args;
  if (es) return { value: es, persist: false, source: 'user' };
  if (confirmed) return { value: confirmed, persist: false, source: 'confirmed' };
  if (anomalyStart) return { value: anomalyStart, persist: true, source: 'anomaly' };
  return { value: undefined, persist: false, source: 'none' };
}
